import * as React from 'react';
import { I18n } from 'react-redux-i18n';

import { Actions } from './components/actions';
import { Flex } from '@common/components/Flex';
import { Questionnaires } from './components/questionnaires';
import { useController } from './controllers';
import { MessageBox } from '@common/components/MessageBox';
import { QuestionnaireManagement } from './components/questionnaire-management';
import { getFolders, getModuleFolders, GetModuleFoldersParams } from '@common/actions/folders';
import { getEmailTemplates } from '@common/actions/invites';
import { QuestionnairePageListDto } from '@common/dto/questionnaire/page/list-dto';
import { FolderContext } from '../../common/dto/folder/context';
import { FolderType } from '../../common/dto/folder/type';
import { getFhq4QuestionnaireField } from './services/questionnaires';
import { ImportQuestionnaireManagement } from './components/import-questionnaire-management';

import './fhq-design-tool.styles.scss';

export const FhqDesignTool: React.FC = () => {
  const {
    handleExportQuestionnaire,
    addLocalQuestionnaireToStartCreatingProcess,
    markPageLoaded,
    handleGetPage,
    isPageLoaded,
    loadingQuestionnaire,
    getDatasheetFolderFormats,
    getFoldersForDatasheet,
    getFolderFields,
    validateQuestionnaireProcessed,
    questionnaireManagementValidationErrors,
    questionnaireManagementValidationInProgress,
    showErrorMessage,
    showConfirmMessage,
    handleLocalQuestionnaireChange,
    handleQuestionnaireSave,
    handleQuestionnaireCopy,
    globalOperationIsLoading,
    handleQuestionnaireDelete,
    closeMessage,
    messageProps,
    selectedQuestionnaireFromList,
    questionnaireForManagement,
    handleSelectQuestionnaireFromList,
    questionnaires,
    questionnairesLoading,
    questionnairesRef,
    currentQuestionnaireIsNotCreated,
    handleCloseImportQuestionnaireManagement,
    supportedQuestionnaireFileExtensions,
    importedQuestionnaireFile,
    handleImportQuestionnaireFile,
    handleImportedFileChange,
    saveImportedQuestionnaire,
    importQuestionnaireFieldRef,
    folders,
    isFoldersLoading
  } = useController();

  return (
    <div className="FhqDesignTool">
      <MessageBox
        hideMessageBox={closeMessage}
        {...messageProps}
      />
      <input
        className="FhqDesignTool__questionnaire-import-field"
        type="file"
        accept={supportedQuestionnaireFileExtensions}
        ref={importQuestionnaireFieldRef}
        onChange={handleImportedFileChange}
      />
      {importedQuestionnaireFile && (
        <ImportQuestionnaireManagement
          onClose={handleCloseImportQuestionnaireManagement}
          onSubmit={saveImportedQuestionnaire}
          importedQuestionnaireFile={importedQuestionnaireFile}
          folders={folders}
          isFoldersLoading={isFoldersLoading}
        />
      )}
      <Actions 
        className="FhqDesignTool__actions"
        disabledState={{
          save: !questionnaireForManagement || globalOperationIsLoading || questionnaireManagementValidationInProgress,
          newQuestionnaire: globalOperationIsLoading || currentQuestionnaireIsNotCreated,
          deleteQuestionnaire: !selectedQuestionnaireFromList || globalOperationIsLoading || currentQuestionnaireIsNotCreated,
          copyQuestionnaire: !selectedQuestionnaireFromList || globalOperationIsLoading || currentQuestionnaireIsNotCreated,
          export: !selectedQuestionnaireFromList || globalOperationIsLoading || currentQuestionnaireIsNotCreated,
          import: globalOperationIsLoading || currentQuestionnaireIsNotCreated,
        }}
        onQuestionnaireExport={handleExportQuestionnaire}
        onQuestionnaireCreate={addLocalQuestionnaireToStartCreatingProcess}
        onQuestionnaireDelete={handleQuestionnaireDelete}
        onQuestionnaireSave={handleQuestionnaireSave}
        onQuestionnaireCopy={handleQuestionnaireCopy}
        onQuestionnaireImport={handleImportQuestionnaireFile}
      />
      <Flex className="FhqDesignTool__container">
        <Questionnaires 
          questionnairesRef={questionnairesRef}
          disabled={globalOperationIsLoading}
          className="FhqDesignTool__container__questionnaires"
          questionnaires={questionnaires}
          loading={questionnairesLoading}
          selectedQuestionnaire={selectedQuestionnaireFromList}
          onSelectedQuestionnaireChange={handleSelectQuestionnaireFromList}
        />
        <QuestionnaireManagement 
          getFhq4QuestionnaireField={getFhq4QuestionnaireField}
          markPageLoaded={markPageLoaded}
          isPageLoaded={isPageLoaded}
          getPage={handleGetPage as <T>(pageId: QuestionnairePageListDto['id']) => Promise<T>}
          loading={loadingQuestionnaire}
          validateQuestionnaire={validateQuestionnaireProcessed}
          errors={questionnaireManagementValidationErrors}
          className="FhqDesignTool__container__questionnaire-management"
          disabled={globalOperationIsLoading}
          questionnaire={questionnaireForManagement}
          showErrorMessage={showErrorMessage}
          showConfirmMessage={showConfirmMessage}
          getBaseFolders={() => getFolders({ context: FolderContext.individual })}
          getFolders={(params?: GetModuleFoldersParams) => getModuleFolders({
            folderTypeId: params?.folderTypeId || FolderType.individual,
            systemFolderConfig: params?.systemFolderConfig,
            setUp: {
              rootFolderName: params?.setUp?.rootFolderName || I18n.t('Individual Data Folders'),
              rootFolderImgUrl: params?.setUp?.rootFolderImgUrl || 'gif/df_individual.gif',
            },
          })}
          getFoldersForDatasheetFormats={getFoldersForDatasheet}
          getDatasheetFolderFormats={getDatasheetFolderFormats}
          getFolderFields={({ folderId, searchQuery, contextId = FolderContext.individual, includeTables }) => getFolderFields({ folderId, contextId, searchQuery, includeTables })}
          getEmailTemplates={getEmailTemplates}
          onChange={handleLocalQuestionnaireChange}
        />
      </Flex>
    </div>
  );
};